import { LinkIcon } from "@xola/icons";
import { Button, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, DemographicIcon, formatDate, Loader } from "common";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { Except } from "type-fest";
import { Guest, Purchase, PurchaseItem, Waiver, WaiverParticipant } from "types";
import { useDebounce } from "use-debounce";
import { useSeller } from "../../../hooks/useAccount";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { NoTranslate } from "../../Common/NoTranslate";
import { SearchInput } from "../../Common/SearchInput";
import { linkWaiverToPurchase } from "./LinkWaiverModal";
import { useIsParticipantMinor } from "@/models/Waiver";

interface WaiverModalInterface {
    purchaseItem: PurchaseItem;
    isOpen: boolean;
    onClose: () => void;
    guest: Guest;
}

export const LinkWaiverParticipantModal = (props: WaiverModalInterface) => {
    const handleClose = () => {
        props.onClose();
    };

    return (
        <Modal isOpen={props.isOpen} onClose={handleClose}>
            <ModalContent {...props} />
        </Modal>
    );
};

export const ModalContent = ({
    onClose,
    purchaseItem: activePurchaseItem,
    guest,
}: Except<WaiverModalInterface, "isOpen">) => {
    const seller = useSeller();
    const reusePastWaiver = seller.preferences?.waiver?.multiLinkDayInterval !== undefined;
    // pass unlinked as true if reusePastWaiver is false (not enabled) or if multiLinkDayInterval is set
    const unlinked = !!seller.preferences?.waiver?.multiLinkDayInterval || !reusePastWaiver;

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch] = useDebounce(searchQuery, 1000);
    const [selectedParticipant, setSelectedParticipant] = useState<WaiverParticipant | null>(null);

    const [newLinkWaiver, setNewLinkedWaiver] = useState<Waiver | undefined>();
    const [unLinkedIds, setUnlinkIds] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const invalidate = useInvalidate();

    const { data: purchase, isLoading: isLoadingPurchase } = useResource<Purchase>(
        ["purchases", activePurchaseItem?.purchase.id],
        { include: "form", expand: "waiver" },
    );

    const fetchParams = searchQuery.length > 0 ? { search: debouncedSearch } : null;

    const {
        data: waivers,
        hasNextPage,
        fetchNextPage,
        isLoading: isLoadingWaivers,
    } = useInfiniteCollection<Waiver>("waivers", { unlinked, ...fetchParams });

    if (isLoadingPurchase) {
        return <Loader />;
    }

    const purchaseItem = purchase?.items.find((item) => item.id === activePurchaseItem.id) ?? purchase?.items[0];

    const handleLinkWaiver = (waiver: Waiver, participant: WaiverParticipant) => {
        setNewLinkedWaiver(waiver);
        setUnlinkIds(unLinkedIds.filter((x) => x !== waiver.id));
        setSelectedParticipant(participant);
    };

    const handleSubmit = async () => {
        if (purchase && newLinkWaiver) {
            setIsLoading(true);

            await linkWaiverToPurchase(
                newLinkWaiver.id,
                purchase.id,
                purchaseItem?.id,
                guest?.id,
                selectedParticipant?.id,
            );

            invalidate("purchases", purchase.id);
            if (purchaseItem?.id) invalidate("purchaseItems", purchaseItem.id);
            invalidate("timeslots");
            if (purchaseItem?.event?.id) invalidate("events", purchaseItem.event.id);
            setIsLoading(false);
            onClose();
        }
    };

    const linkedParticipants =
        purchaseItem?.guests
            .map((guest) => guest.participant)
            .filter((participant): participant is string => participant !== undefined) ?? [];

    return (
        <>
            <Modal.Header>Link Waiver to Attendee</Modal.Header>
            <Modal.Body style={{ height: "400px" }}>
                <div className="flex">
                    <div className="flex-1">
                        <div className="mt-4">
                            <SearchInput
                                size="medium"
                                placeholder="Search by name, email, or phone"
                                onChange={(searchResult: string) => setSearchQuery(searchResult)}
                            />
                        </div>
                        <div className="mt-6">
                            {waivers.length > 0 ? (
                                <Virtuoso
                                    style={{ height: "350px" }}
                                    data={waivers}
                                    endReached={async () => {
                                        if (hasNextPage) {
                                            fetchNextPage();
                                        }
                                    }}
                                    itemContent={(_, waiver) => (
                                        <LinkWaiverParticipantItem
                                            linkedParticipants={linkedParticipants}
                                            waiver={waiver}
                                            selectedParticipant={selectedParticipant}
                                            onItemClick={(waiver, participant) => handleLinkWaiver(waiver, participant)}
                                        />
                                    )}
                                />
                            ) : isLoadingWaivers ? (
                                <Loader />
                            ) : (
                                <div className="flex justify-center">
                                    <span className="pb-4 pt-4 text-lg font-bold text-black">No results found</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="space-x-4">
                <Button color="secondary" variant="outline" onClick={onClose}>
                    Cancel
                </Button>
                <SubmitButton color="primary" disabled={!newLinkWaiver} isLoading={isLoading} onClick={handleSubmit}>
                    Save
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

const LinkWaiverParticipantItem = ({
    waiver,
    onItemClick,
    selectedParticipant,
    linkedParticipants,
}: {
    waiver: Waiver;
    onItemClick: (waiver: Waiver, participant: WaiverParticipant) => void;
    selectedParticipant: WaiverParticipant | null;
    linkedParticipants: ID[];
}) => {
    const userTimeZone = dayjs.tz.guess();
    const linkedWaiversCount = waiver.purchases?.length;
    const completeLinkedCount = linkedWaiversCount ?? 0;

    return (
        <>
            {waiver.participants.map((participant) => {
                const isLinked = linkedParticipants.includes(participant.id);
                return (
                    <Tooltip
                        key={participant.id}
                        disabled={!isLinked}
                        content={isLinked && "Waiver participant already assigned to the purchase"}
                    >
                        <div
                            className={clsx(
                                participant === selectedParticipant && "bg-primary text-white",
                                "cursor-pointer border-b border-gray-light p-2",
                                isLinked && "bg-secondary text-white",
                            )}
                            onClick={() => !isLinked && onItemClick(waiver, participant)}
                        >
                            <div className="mr-auto flex items-center">
                                <WaiverParticipantDetails
                                    waiver={waiver}
                                    participant={participant}
                                    size="tiny"
                                    nameMaxLength={20}
                                />

                                {completeLinkedCount > 0 && (
                                    <div className="ml-2 mr-4 flex items-center justify-center rounded-full bg-gray-lighter px-2 py-1 text-gray-dark ">
                                        <LinkIcon size="sm" />
                                        <NoTranslate as="div" className="ml-1 text-sm">
                                            {completeLinkedCount}
                                        </NoTranslate>
                                    </div>
                                )}
                            </div>

                            <div className="text-base tracking-tighter text-gray-dark">
                                {formatDate(dayjs(waiver?.createdAt).tz(userTimeZone), DateFormat.DATE_TIME_SHORT)}
                            </div>
                        </div>
                    </Tooltip>
                );
            })}
        </>
    );
};

export const WaiverParticipantDetails = ({
    waiver,
    participant,
    size,
    nameMaxLength,
}: {
    waiver: Waiver;
    participant: WaiverParticipant;
    size?: string;
    nameMaxLength?: number;
}) => {
    const isCustomerNameLengthExceedingMax = nameMaxLength ? participant.customerName.length > nameMaxLength : false;

    const isMinor = useIsParticipantMinor(participant, waiver.waiverTypeId);
    return (
        <div>
            <Tooltip
                key={participant.id}
                content={
                    isMinor
                        ? `${
                              isCustomerNameLengthExceedingMax ? participant.customerName + " " : ""
                          }Minor, DOB: ${formatDate(participant.dateOfBirth, DateFormat.DATE)}`
                        : participant.customerName
                }
                disabled={!isCustomerNameLengthExceedingMax && !isMinor}
            >
                <div key={participant.id} className="flex items-center space-y-0.5">
                    <DemographicIcon name={isMinor ? "child" : "adult"} size={size} className="mt-0.5" />
                    <NoTranslate className="ml-1">
                        {isCustomerNameLengthExceedingMax
                            ? participant.customerName.slice(0, nameMaxLength)
                            : participant.customerName}{" "}
                        {isCustomerNameLengthExceedingMax && "..."}
                    </NoTranslate>
                </div>
            </Tooltip>
        </div>
    );
};
